import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import moment, { Moment } from 'moment';
import MondayMomentUtils from 'utils/MondayMomentUtils';
import 'moment/locale/en-gb';
import { INVALID_DATE, isInvalidDate } from './utils';

const VALUE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'DD/MM/YYYY';

interface DateFieldProps {
  id: string;
  label: string;
  // YYYY-MM-DD, empty, or INVALID_DATE plus the typed text while the input
  // holds no usable date.
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disablePast?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  autoFocus?: boolean;
  // The caller's own error; its helper text then replaces the field's.
  error?: boolean;
  helperText?: ReactNode;
}

const typedText = (value: string) =>
  isInvalidDate(value) ? value.slice(INVALID_DATE.length) : '';

// The date a value stands for; an invalid value yields the typed text parsed
// as entered, so its error shows again when the field mounts anew.
const toMoment = (value: string) => {
  if (!value) {
    return null;
  }
  return isInvalidDate(value)
    ? moment(typedText(value), DISPLAY_FORMAT, true)
    : moment(value, VALUE_FORMAT, true);
};

// The themed picker of single Create assignment for every date field of the
// Assignments module (change request of 07.10, item 6): DD/MM/YYYY in every
// browser, opening on the day view. State, URL parameters and API payloads
// keep YYYY-MM-DD strings. A half-typed, impossible or out-of-range date stays
// in the input with an error and reaches the caller as INVALID_DATE with the
// typed text, so it can never be submitted as the last valid date and still
// shows when a closed row editor opens again.
const DateField = ({
  id,
  label,
  value,
  onChange,
  min,
  max,
  disablePast = false,
  required = false,
  size = 'medium',
  fullWidth = false,
  autoFocus = false,
  error = false,
  helperText,
}: DateFieldProps) => {
  const intl = useIntl();
  const today = moment().format(VALUE_FORMAT);
  const lowest = [min, disablePast ? today : undefined]
    .filter((bound): bound is string => Boolean(bound))
    .sort()
    .pop();

  const problem = (date: Moment | null) => {
    if (!date) {
      return null;
    }
    if (!date.isValid()) {
      return intl.formatMessage({ id: 'ORDERS.DATE.INVALID' });
    }
    if (lowest && date.isBefore(lowest, 'day')) {
      return intl.formatMessage(
        { id: 'ORDERS.DATE.TOO_EARLY' },
        { date: moment(lowest).format(DISPLAY_FORMAT) },
      );
    }
    if (max && date.isAfter(max, 'day')) {
      return intl.formatMessage(
        { id: 'ORDERS.DATE.TOO_LATE' },
        { date: moment(max).format(DISPLAY_FORMAT) },
      );
    }
    return null;
  };

  const toValue = (date: Moment | null, typed?: string | null) => {
    if (!date) {
      return '';
    }
    return problem(date)
      ? `${INVALID_DATE}${typed ?? date.format(DISPLAY_FORMAT)}`
      : date.format(VALUE_FORMAT);
  };

  // The last value this field reported, so the caller echoing it back keeps
  // the typed text, while a value set from outside (prefill, clearing a
  // filter) replaces it.
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  const picked = useMemo(() => toMoment(draft), [draft]);

  const ownProblem = problem(picked);

  return (
    <MuiPickersUtilsProvider
      utils={MondayMomentUtils}
      libInstance={moment}
      locale="en-gb"
    >
      <KeyboardDatePicker
        id={id}
        label={label}
        value={picked}
        onChange={(date: Moment | null, typed?: string | null) => {
          const next = toValue(date, typed);
          setDraft(next);
          onChange(next);
        }}
        invalidLabel={typedText(draft)}
        format={DISPLAY_FORMAT}
        placeholder={DISPLAY_FORMAT}
        inputVariant="outlined"
        size={size}
        openTo="date"
        views={['year', 'month', 'date']}
        autoOk
        clearable={!required}
        minDate={lowest ? moment(lowest) : undefined}
        maxDate={max ? moment(max) : undefined}
        required={required}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        // Explicit values replace the picker's built-in English messages.
        error={error || Boolean(ownProblem)}
        helperText={(error && helperText) || ownProblem || helperText}
        InputLabelProps={{ shrink: true }}
        KeyboardButtonProps={{ 'aria-label': label }}
      />
    </MuiPickersUtilsProvider>
  );
};

export default DateField;
